import { BadgeVariant } from '../components/partials/StatusBadge';

// "InProgress" -> "In Progress"; leaves already-spaced statuses untouched.
export function formatStatusDisplay(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// Maps a real service-request status (plus the needsPayment flag) to a base StatusBadge variant
// (color) and the label to show. Payment takes precedence so an unpaid request reads as actionable.
export function serviceRequestBadge(
  status: string,
  needsPayment: boolean
): { variant: BadgeVariant; label?: string } {
  if (needsPayment) {
    return { variant: 'payment-required' };
  }

  return statusBadge(status);
}

// Plain status -> badge mapping (color + label) for any status string, e.g. checkout order
// statuses (Paid / Pending / Failed / Canceled).
export function statusBadge(status: string): {
  variant: BadgeVariant;
  label?: string;
} {
  return {
    variant: variantForStatus(status),
    label: formatStatusDisplay(status),
  };
}

// General-purpose status -> color mapping. It covers more statuses than any single endpoint
// currently emits (e.g. the checkout endpoint only ever returns "Paid"; active service requests
// never return "Failed"/"Canceled") - the extra cases are intentional, so the helper stays correct
// if those statuses start surfacing, rather than silently falling through to neutral.
function variantForStatus(status: string): BadgeVariant {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'filed':
    case 'active':
    case 'ready to clear':
    case 'paid':
      return 'completed';
    case 'import completed':
    case 'importcompleted':
    case 'imported':
      return 'import-completed';
    case 'in progress':
    case 'inprogress':
    case 'upcoming':
    case 'signed':
    case 'signature required':
      return 'in-progress';
    case 'pending':
    case 'awaiting response':
    case 'awaiting responses':
    case 'awaiting resolutions':
    case 'awaiting asset data':
      return 'pending';
    case 'incomplete':
    case 'incomplete paid':
    case 'not started':
      return 'incomplete';
    // 'past due' is a live subscription that is not in good standing (past_due / unpaid /
    // paused) — the customer needs to act, so it shares the errored treatment.
    case 'errored':
    case 'needs attention':
    case 'failed':
    case 'past due':
      return 'errored';
    // Still active and still billing, but scheduled to stop. Shares the "needs a look"
    // treatment rather than the terminal neutral of 'canceled'.
    case 'ending':
      return 'pending';
    case 'canceled':
    case 'cancelled':
      return 'neutral';
    default:
      return 'neutral';
  }
}
